/**
 * The legal pages' facts, for any screen that needs them. The legal pages
 * read it, and so do the footer links and the account menu's "Legal" entry
 * (`components/LegalFooter`, legal-pages ticket 05), the sign-in agreement
 * box and the in-app agreement step (ticket 03), the upload notice's agreement
 * sentence (ticket 04) and the checkout confirmation (ticket 07).
 *
 *   import { legal } from '../../lib/legal';
 *
 *   legal.published      false until the owner sets PUBLISHED = true. While
 *                        false, nothing outside the legal pages mentions the terms.
 *   legal.version        the terms version someone agrees to ("" until set)
 *   legal.effectiveDate  as written in the config, e.g. "January 15, 2027"
 *   legal.entity         the company's registered name ("" until set)
 *   legal.documents      one per page, in hub order: slug, title, description, path
 *   legal.sections       a section the app links to by name (today: `billing`,
 *                        section 10 of the Terms): slug, the old page's path, and
 *                        `to`, the section's address
 *
 * Every value comes from `frontend/legal/config.toml` and `legal/docs/*.md`
 * through `scripts/legal.mjs`, which `npm run dev`, `build` and `typecheck`
 * run first. `PUBLISHED = true` cannot build with an empty value, so a
 * published `version`, `effectiveDate` and `entity` are never "".
 *
 * A document's text is not here on purpose: it is `legalContent[slug]` in
 * `features/legal/content.generated.ts`, loaded only with the legal pages, so
 * importing this module costs a screen a few hundred bytes.
 */
export interface LegalDocument {
  /** The document's name, and what the server records an agreement under: `terms`, `privacy`, `auto-apply`. */
  slug: string;
  /** The document's own title, from its `# ` heading. */
  title: string;
  /** One line saying what the document covers. */
  description: string;
  /** Where it lives in the app: `/terms`, `/privacy`, `/auto-apply-terms`. */
  path: string;
}

/**
 * A section of a document that the app links to by name. `billing` is the
 * Subscription, Credits and Refund Terms: a page of their own until 2026-10-06,
 * section 10 of the Terms since (`SECTIONS` in `scripts/legal.mjs`).
 */
export interface LegalSection {
  /** `billing`: what the server records the checkout's agreement under. */
  slug: string;
  /** The address the section had as a page (`/billing-terms`). The app sends it on to `to`. */
  path: string;
  /** Where the section is now, e.g. `/terms#10-subscription-credits-and-refunds`. */
  to: string;
}

export interface Legal {
  published: boolean;
  version: string;
  effectiveDate: string;
  entity: string;
  documents: LegalDocument[];
  sections: LegalSection[];
}

/** One document's text: HTML built from its markdown, and its `##` sections. */
export interface LegalContent {
  html: string;
  headings: { id: string; text: string }[];
}

import { generatedLegal } from './legal.generated';

/**
 * DEV SERVER ONLY: how to see the screens that wait for `published` without
 * touching `legal/config.toml` (whose values are the owner's and counsel's).
 * In the browser console of `npm run dev` or `dev:mock`:
 *
 *   localStorage.setItem('cj_dev_legal', JSON.stringify({ published: true, version: 'dev-1', entity: 'ClimbJobs Dev LLC', effectiveDate: 'January 1, 2027' }))
 *
 * then reload; `localStorage.removeItem('cj_dev_legal')` and a reload undo it.
 * `scripts/flows.mjs` sets the same key for its published steps. Only these
 * facts change: the legal pages themselves keep their draft banner, which
 * comes from the build. `import.meta.env.DEV` is false in a production build,
 * where this function is dead code: nothing a visitor stores can turn the
 * agreement boxes on, or off.
 */
export const DEV_LEGAL_KEY = 'cj_dev_legal'; // `flows.mjs` reads this
function devOverride(): Partial<Legal> {
  // `?.`: outside Vite (a node script reading this module) there is no `env` at all.
  if (!import.meta.env?.DEV) return {};
  try {
    const raw = window.localStorage.getItem(DEV_LEGAL_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Partial<Legal>) : {};
  } catch {
    return {};
  }
}

export const legal: Legal = { ...generatedLegal, ...devOverride() };
